import { Box } from '@hivebrite/design-system/Box';
import { IconHero, IconSmallHero } from '@hivebrite/design-system/Icon';
import { Loader } from '@hivebrite/design-system/Loader';
import { convertRemToPx } from '@hivebrite/design-system/Theme';

import { Children } from 'hb-builder/components/common/Item';
import type { RenderProps } from 'hb-builder/components/common/type/RenderProps';
import { defaultTabs } from 'hb-builder/constants/tabs';
import {
  useComponentContext,
  useComponentsContext,
  useRendererContext,
} from 'hb-builder/hooks';
import type { BuilderComponent } from 'hb-builder/types';
import type { DocumentItem } from 'hb-react/backoffice/types/builder/builderDocument';
import type { HeroItemAttributes } from 'hb-react/backoffice/types/builder/itemAttributes/heroItemAttributes';

import Settings from './Settings';

const Hero: BuilderComponent<HeroItemAttributes> = () => <div />; // never used

const RenderBuilder = () => {
  const { item } = useComponentContext();
  const { components } = useComponentsContext();

  const attributes = item?.attributes;

  const { Section } = components;

  return attributes?.isLoadingSrc ? (
    <Box height="300px">
      <Loader size={40} />
    </Box>
  ) : (
    <Section.RenderBuilder attributes={attributes}>
      <Children item={item} />
    </Section.RenderBuilder>
  );
};

const Render = ({
  children,
  item,
}: RenderProps<DocumentItem<HeroItemAttributes>>) => {
  const rendererContextValue = useRendererContext();
  const { components } = useComponentsContext();

  const { Section } = components;

  return (
    <Section.Render
      components={components}
      item={item}
      {...rendererContextValue}
    >
      {children}
    </Section.Render>
  );
};

Hero.componentFamily = 'Hero';
Hero.componentName = 'Hero';
Hero.componentType = 'section';

Hero.componentLabel = __('Hero');
Hero.componentCategory = 'block';

Hero.componentClickable = true;
Hero.canBeDoubleClicked = true;
Hero.canBeDragged = true;
Hero.canBeListed = true;
Hero.canBeSelected = true;

const defaultConfig = (props) => {
  const { bodyWidth, components, features, theme } = props;
  const { Button, Buttons, Column, Text } = components;

  const headline = Text.item({
    ...props,
    features: {
      ...features,
      text: {
        ...features?.text,
        containerPadding: 0,
        content: __('Headline goes here'),
        defaultTextColor: true,
        horizontalAlign: 'center',
        textColor: '#fff',
        textFontSize: convertRemToPx(theme?.fontSizes[10]),
        tied: true,
      },
    },
  });

  const subline = Text.item({
    ...props,
    features: {
      ...features,
      text: {
        ...features?.text,
        containerPadding: 0,
        content: __(
          'Subline goes here, you can write as much as you want here.',
        ),
        defaultTextColor: true,
        horizontalAlign: 'center',
        textColor: '#fff',
        textFontSize: convertRemToPx(theme?.fontSizes[2]),
        tied: true,
      },
    },
  });

  const button =
    // This component is used for both Email and Page Builder.
    // Email Builder does not support `Buttons`.
    Buttons
      ? Buttons.item({
          ...props,
          features: {
            ...features,
            buttons: {
              ...features?.buttons,
              containerPadding: '32px 0px 16px 0px',
              tied: true,
            },
          },
        })
      : Button.item({
          ...props,
          features: {
            ...features,
            button: {
              ...features?.button,
              containerPadding: '32px 0px 16px 0px',
              horizontalAlign: 'center',
              tied: true,
            },
          },
        });

  const column = Column.item({
    ...props,
    children: [headline, subline, button],
    disableSelection: true,
    verticalAlign: 'middle',
  });

  return {
    children: [column],
    containerBackgroundVideo: '',
    containerBackgroundImage:
      'https://images.unsplash.com/photo-1564359179886-afa7f541e3b8?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=crop&w=2555&q=80',
    containerBackgroundImageAlt: '',
    containerBackgroundSize: 'cover',
    flexDirection: 'column',
    horizontalAlign: 'center',
    name: Hero.componentName,
    sectionWidth: bodyWidth,
    type: Hero.componentName,
    containerPadding: '60px 20px',
  };
};

Hero.item = (props) => {
  const { components, features } = props;
  const heroConfig = {
    ...defaultConfig(props),
    ...(features?.hero || {}),
  };

  const { children, ...restHeroConfig } = heroConfig;
  const { Section } = components;

  return Section.item({
    ...props,
    children,
    components,
    features: {
      ...features,
      section: {
        ...features?.section,
        ...restHeroConfig,
      },
    },
  });
};

Hero.componentIcon = ({ config } = {}) =>
  config?.features?.panel?.applyNewDesign ? IconSmallHero : IconHero;

Hero.toolbar = ['edit', 'drag', 'duplicate', 'delete'];

Hero.Settings = Settings;
Hero.SettingsTabs = defaultTabs;

Hero.RenderBuilder = RenderBuilder;
Hero.Render = Render;

export default Hero;
